import { Clear as ClearIcon } from '@mui/icons-material';
import {
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	Theme,
	Typography,
} from '@mui/material';
import { useMutation } from '@parkchirp/api-client';
import React, { FC, useCallback, useState } from 'react';

import { CREATE_SEQUENCING_TIER } from '../../../mutations';
import { SetShowSnackbar } from '../../../types';
import { BaseTierRate } from '../../../types/leases';
import { EditorModal } from '../../modals';

type AddBaseTierProps = {
	subscriptionId: string;
	rates: BaseTierRate[];
	theme: Theme;
	setNumber: number;
	setShowSnackbar: SetShowSnackbar;
	handleClose: () => void;
	pollSequencingTiers: () => Promise<void>;
};

export const AddBaseTierModal: FC<AddBaseTierProps> = ({
	subscriptionId,
	rates,
	theme,
	setNumber,
	setShowSnackbar,
	handleClose,
	pollSequencingTiers,
}) => {
	const [rateId, setRateId] = useState<string>(null);
	const [sublotId, setSublotId] = useState<number>(null);
	const [createSequencingTier, { loading: createInProgress }] = useMutation(CREATE_SEQUENCING_TIER);

	const sublotOptions = useCallback(() => {
		if (!rates || !rateId) {
			return [];
		}

		const rate = rates.find((r) => r.rateId === rateId);
		return rate.sublots;
	}, [rates, rateId]);

	const validateTier = useCallback(() => {
		const invalidFields: string[] = [];

		if (!rateId) {
			invalidFields.push('Rate');
		}

		if (!sublotId) {
			invalidFields.push('Sublot');
		}

		return invalidFields;
	}, [rateId, sublotId]);

	const handleSave = useCallback(async () => {
		try {
			const invalidFields = validateTier();

			if (invalidFields.length) {
				setShowSnackbar({
					message: `Required Field(s): ${invalidFields.join(', ')}`,
					severity: 'error',
					autoHideDuration: null,
				});
				return;
			}

			await createSequencingTier({
				variables: {
					subscriptionId,
					rateId,
					sublotId,
					leasingCountAndRateId: null,
					leasingConversionRightId: null,
					tierNumber: 0,
					setNumber,
				},
				context: { apiVersion: 'v2' },
				onCompleted: async () => {
					setShowSnackbar({
						message: 'Tier successfully added',
						severity: 'success',
						autoHideDuration: 3000,
					});
					handleClose();
					await pollSequencingTiers();
				},
			});
		} catch {
			setShowSnackbar({
				message: 'Failed to add sequencing tier',
				severity: 'error',
				autoHideDuration: 5000,
			});
		}
	}, [
		validateTier,
		createSequencingTier,
		subscriptionId,
		rateId,
		sublotId,
		setNumber,
		setShowSnackbar,
		handleClose,
		pollSequencingTiers,
	]);

	return (
		<EditorModal
			handleClose={handleClose}
			handleSave={handleSave}
			headerText='Add Base Tier'
			headerBorder
			loading={createInProgress}
			theme={theme}
		>
			<Grid
				container
				spacing={2}
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignSelf: 'center',
					mt: 2,
					mb: 2,
				}}
			>
				<Grid item xs={12} mb={2}>
					<Typography variant='body1'>Select a rate/sublot combination to add to the base tier.</Typography>
				</Grid>
				<Grid item xs={6}>
					<FormControl fullWidth variant='outlined' required>
						<InputLabel id='rate-label'>Rate</InputLabel>
						<Select
							fullWidth
							labelId='rate-select-label'
							id='rate-select'
							value={rateId || ''}
							label='Rate'
							disabled={false}
							onChange={(e) => setRateId(e.target.value)}
							endAdornment={
								rateId && (
									<InputAdornment sx={{ position: 'absolute', right: 16 }} position='end'>
										<IconButton
											onClick={() => {
												setRateId(null);
												setSublotId(null);
											}}
										>
											<ClearIcon />
										</IconButton>
									</InputAdornment>
								)
							}
						>
							{rates.map(({ rateId: key, rateName }) => (
								<MenuItem key={key} value={key}>
									{rateName}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={6}>
					<FormControl fullWidth variant='outlined' required>
						<InputLabel id='sublot-label'>Sublot</InputLabel>
						<Select
							fullWidth
							labelId='sublot-select-label'
							id='sublot-select'
							value={sublotId}
							label='Sublot'
							disabled={false}
							onChange={(e) => setSublotId(Number(e.target.value))}
							endAdornment={
								sublotId && (
									<InputAdornment sx={{ position: 'absolute', right: 16 }} position='end'>
										<IconButton
											onClick={() => {
												setSublotId(null);
											}}
										>
											<ClearIcon />
										</IconButton>
									</InputAdornment>
								)
							}
						>
							{sublotOptions().map(({ sublotId: key, sublotName }) => (
								<MenuItem key={key} value={key}>
									{sublotName}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
		</EditorModal>
	);
};
